import React, { useState } from 'react';
import { View } from 'react-native';
import moment from 'moment';
import MyDataTable from '../../../components/MyDataTable';
import MyAppText from '../../../components/MyAppText';
import { translate } from '../../../services/translate';
import { auditLogsService } from '../../../services/central-api/audit-logs';
import { LogsParamList } from '../../../typings/Params';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import { Pagination } from '../../../components/Pagination';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';
import Toast from 'react-native-toast-message';

export default function LogList({ navigation }: { navigation: Navigation; }) {
    const { styles } = useStyles(styleSheet);
    const route = useRoute<RouteProp<LogsParamList, 'LogList'>>();
    const rawPage = Number(route.params.page);
    const rawLimit = Number(route.params.limit);
    const initialPage = Number.isNaN(rawPage) ? 0 : Math.max(rawPage - 1, 0);
    const initialLimit = Number.isNaN(rawLimit) ? 25 : rawLimit;

    const [filters, setFilters] = useFilters<LogsParamList['LogList']>({
        ...route.params,
        page: initialPage,
        limit: initialLimit,
    });
    const [sortBy, setSortBy] = useState<'asc' | 'desc'>(filters.sort || 'desc');

    const isFocused = useIsFocused();
    const { data, isLoading } = useQuery({
        queryKey: ['getLogs', filters],
        queryFn: () => auditLogsService.getLogs(filters),
        enabled: isFocused,
        onError: () => {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedErrorDecreaseTimeRange'),
                visibilityTime: 10000,
            });
        },
    });

    function formatDetailValue(key: string, value: unknown) {
        if (/date/i.test(key) && value) {
            return moment(value as string | number | Date).format('DD/MM/YYYY HH:mm:ss');
        }

        if (typeof value === 'object') {
            return JSON.stringify(value);
        }

        return String(value);
    }

    const totalItems = (data ?? []).length === filters.limit
        ? (filters.page + 2) * filters.limit
        : filters.page * filters.limit + (data ?? []).length;

    return <Container>
        <Header
            title={translate('logRegistries')}
        />
        <Card>
            <DataTableHeader
                zIndex={2}
                title={translate('registeredLogs')}
                filter={<Filter setSortBy={setSortBy} sortBy={sortBy} filters={filters} setFilters={setFilters} />}
            />
            <DataTableContainer
                dataTable={<MyDataTable
                    columns={[{
                        name: translate('date'),
                        selector: row =>
                            moment(row.date).format('DD/MM/YYYY HH:mm:ss'),
                        sortable: true,
                    },
                    {
                        name: translate('user'),
                        selector: row =>
                            row.userId === -1 && row.userName
                                ? translate(row.userName)
                                : row.userId
                                    ? `${row.userId} - ${row.userName}`
                                    : translate('notApplicable'),
                    },
                    {
                        name: translate('ipAddress'),
                        selector: row => row.ip ?? '',
                    },
                    {
                        name: translate('action'),
                        selector: row => translate(row.action),
                        wrap: true
                    },]}
                    data={data ?? []}
                    expandableRows
                    expandableRowsComponent={({ data: log }) => (
                        <View style={styles.detailTable}>
                            {Object.entries(log.detail ?? {})
                                .filter(([k]) => k !== 'company' && k !== 'companyId')
                                .map(([k, v]) => (
                                    <View key={k} style={styles.detailRow}>
                                        <MyAppText style={styles.detailKey}>{k}</MyAppText>
                                        <MyAppText style={styles.detailValue}>
                                            {formatDetailValue(k, v)}
                                        </MyAppText>
                                    </View>
                                ))}
                        </View>
                    )}
                    progressPending={isLoading}
                />}
                pagination={<Pagination
                    currentPage={filters.page}
                    totalItems={totalItems}
                    setPage={page => {
                        setFilters(old => ({ ...old, page }));
                        navigation.setParams({
                            ...route.params,
                            page: page + 1,
                        });
                    }}
                    pageSize={filters.limit}
                    setPageSize={pageSize => {
                        setFilters(old => ({ ...old, limit: pageSize, page: 0 }));
                        navigation.setParams({
                            ...route.params,
                            limit: pageSize,
                            page: 1,
                        });
                    }}
                    hideTotalItemsAndPagesInfo={true}
                />}
            />
        </Card>
    </Container>;
}

const styleSheet = createStyleSheet(theme => ({
    detailTable: {
        backgroundColor: theme.colors.container.background,
        padding: 8,
        flex: 1,
    },
    detailRow: {
        flexDirection: 'row',
        paddingVertical: 4,
        borderBottomWidth: 1,
        borderColor: theme.colors.card.border,
    },
    detailKey: {
        flex: 1,
        fontSize: 12,
        fontWeight: '600',
    },
    detailValue: {
        flex: 2,
        fontSize: 12,
        maxWidth: 700,
    },
}));
